// AdminGelatoProductsPage — gestion du catalogue synchronisé depuis Etsy via Gelato.
// Fonctionnalités : re-sync, édition des prix retail par variant, toggle published.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, RefreshCw, Eye, EyeOff, Check, X, ExternalLink, AlertCircle, Search, Trash2, Tag, Sparkles } from "lucide-react";
import { adminApi } from "@/lib/adminApi";

const fmtEUR = (cents) =>
  cents == null ? "—" : new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(cents / 100);

const fmtRelative = (iso) => {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h`;
  const d = Math.floor(h / 24);
  return `${d} j`;
};

const AdminGelatoProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [pList, sList] = await Promise.all([
        adminApi.listGelatoProducts(),
        adminApi.listGelatoStores(),
      ]);
      setProducts(pList.items || []);
      setStores(sList.stores || []);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSync = async (storeId) => {
    setSyncing(true);
    setSyncResult(null);
    setError(null);
    try {
      const out = await adminApi.syncGelatoStore(storeId);
      setSyncResult(out);
      await load();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSyncing(false);
    }
  };

  const handlePriceSave = async (productId, variantId, newCents) => {
    try {
      await adminApi.overrideVariantPrice(productId, variantId, newCents);
      setProducts((prev) =>
        prev.map((p) =>
          p.product_id === productId
            ? {
                ...p,
                variants: p.variants.map((v) =>
                  v.variant_id === variantId
                    ? { ...v, retail_amount_cents: newCents, manual_override: true }
                    : v,
                ),
              }
            : p,
        ),
      );
    } catch (e) {
      setError(e.message || String(e));
    }
  };

  const handlePublishToggle = async (productId, current) => {
    try {
      await adminApi.setProductPublished(productId, !current);
      setProducts((prev) =>
        prev.map((p) => (p.product_id === productId ? { ...p, published: !current } : p)),
      );
    } catch (e) {
      setError(e.message || String(e));
    }
  };

  const handleDelete = async (productId, title) => {
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      `Supprimer définitivement "${title}" de la boutique ?\n\n` +
      `Le produit sera retiré du cache et ne réapparaîtra plus lors des prochains re-sync ` +
      `Gelato tant qu'il n'aura pas été explicitement restauré depuis l'admin.`
    );
    if (!ok) return;
    try {
      await adminApi.deleteGelatoProduct(productId);
      setProducts((prev) => prev.filter((p) => p.product_id !== productId));
    } catch (e) {
      setError(e.message || String(e));
    }
  };

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return products;
    return products.filter((p) => (p.title || "").toLowerCase().includes(query));
  }, [products, q]);

  return (
    <div className="space-y-6" data-testid="admin-gelato-products-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl text-[color:var(--gv-ink)] mb-1">Catalogue Etsy / Gelato</h1>
          <p className="text-[13px] text-[color:var(--gv-ink)]/65">
            {products.length} produits synchronisés depuis Etsy via Gelato. Édite les prix retail pour matcher exactement ton catalogue Etsy.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {stores.map((s) => (
            <button
              key={s.id}
              onClick={() => handleSync(s.id)}
              disabled={syncing}
              className="btn-primary"
              data-testid={`sync-store-${s.type}`}
            >
              {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Re-sync {s.name || s.type}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-[5px] text-[13px] flex items-start gap-2" data-testid="admin-error">
          <AlertCircle size={16} className="shrink-0 mt-0.5" /> {error}
        </div>
      )}
      {syncResult && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-[5px] text-[13px]" data-testid="sync-result">
          ✓ Sync OK — {syncResult.upserted} produits upserted, {syncResult.failed} échoués, {syncResult.variant_calls || 0} appels variant
        </div>
      )}

      <div className="relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--gv-ink)]/40" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filtrer par titre…"
          className="gv-input w-full pl-9"
          data-testid="products-search"
        />
      </div>

      {loading ? (
        <div className="py-16 text-center"><Loader2 size={24} className="animate-spin mx-auto text-[color:var(--gv-pink)]" /></div>
      ) : (
        <div className="space-y-3">
          {filtered.map((p) => (
            <ProductRow
              key={p.product_id}
              product={p}
              expanded={!!expanded[p.product_id]}
              onToggle={() => setExpanded((e) => ({ ...e, [p.product_id]: !e[p.product_id] }))}
              onPriceSave={handlePriceSave}
              onPublishToggle={handlePublishToggle}
              onDelete={handleDelete}
            />
          ))}
          {filtered.length === 0 && (
            <div className="py-12 text-center text-[color:var(--gv-ink)]/50 text-[13px]">Aucun produit.</div>
          )}
        </div>
      )}
    </div>
  );
};

const ProductRow = ({ product, expanded, onToggle, onPriceSave, onPublishToggle, onDelete }) => {
  const minPrice = product.variants?.reduce(
    (acc, v) => (v.retail_amount_cents != null && (acc == null || v.retail_amount_cents < acc) ? v.retail_amount_cents : acc),
    null,
  );
  // Editorial meta editor — badge + "pourquoi cette carte ?" bullets.
  // Lazy-load: we only hit /admin/shop/meta/{id} when the modal opens
  // so the products page stays fast even with 200+ items.
  const [metaOpen, setMetaOpen] = useState(false);

  return (
    <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] overflow-hidden" data-testid={`product-row-${product.product_id}`}>
      <div className="flex items-center gap-4 p-3">
        <img src={product.preview_url || product.external_preview_url}
             alt={product.title}
             className="w-16 h-20 object-cover rounded-[3px] bg-[color:var(--gv-mist)] shrink-0"
             onError={(e) => { e.currentTarget.style.display = "none"; }} />
        <div className="flex-1 min-w-0">
          <div className="font-display text-[15px] text-[color:var(--gv-ink)] line-clamp-2 mb-1">{product.title}</div>
          <div className="flex items-center gap-3 text-[11.5px] text-[color:var(--gv-ink)]/55">
            <span>{product.variants?.length || 0} variants</span>
            <span>·</span>
            <span>from {fmtEUR(minPrice)}</span>
            <span>·</span>
            <span>synced {fmtRelative(product.synced_at)} ago</span>
            {product.external_id && (
              <a href={`https://www.etsy.com/listing/${product.external_id}`} target="_blank" rel="noreferrer"
                 className="hover:text-[color:var(--gv-pink)] inline-flex items-center gap-1">
                Etsy <ExternalLink size={10} />
              </a>
            )}
          </div>
        </div>
        <button
          onClick={() => onPublishToggle(product.product_id, product.published)}
          className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[4px] text-[11.5px] border transition-colors ${
            product.published
              ? "bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
              : "bg-[color:var(--gv-mist)] border-[color:var(--gv-line)] text-[color:var(--gv-ink)]/55 hover:bg-gray-100"
          }`}
          data-testid={`publish-toggle-${product.product_id}`}
        >
          {product.published ? <><Eye size={11} /> Publié</> : <><EyeOff size={11} /> Caché</>}
        </button>
        <button
          onClick={() => onDelete(product.product_id, product.title)}
          className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[4px] text-[11.5px] border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
          title="Supprimer définitivement de la boutique (avec confirmation)"
          data-testid={`delete-product-${product.product_id}`}
        >
          <Trash2 size={11} /> Supprimer
        </button>
        <button
          onClick={() => setMetaOpen(true)}
          className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[4px] text-[11.5px] border border-[color:var(--gv-line)] text-[color:var(--gv-ink)]/75 bg-white hover:bg-[color:var(--gv-mist)] transition-colors"
          title="Badge, arguments “Pourquoi cette carte ?”"
          data-testid={`meta-edit-${product.product_id}`}
        >
          <Tag size={11} /> Meta
        </button>
        <button onClick={onToggle} className="text-[12px] text-[color:var(--gv-blue)] hover:underline px-2"
                data-testid={`expand-${product.product_id}`}>
          {expanded ? "Réduire" : "Variants ▾"}
        </button>
      </div>

      {metaOpen && (
        <ShopMetaModal
          productId={product.product_id}
          title={product.title}
          onClose={() => setMetaOpen(false)}
        />
      )}

      {expanded && (
        <div className="border-t border-[color:var(--gv-line)] bg-[color:var(--gv-mist)]/30">
          <table className="w-full text-[13px]">
            <thead className="text-[10.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/45">
              <tr>
                <th className="text-left px-4 py-2">Variant</th>
                <th className="text-left px-4 py-2">Product UID</th>
                <th className="text-right px-4 py-2">Wholesale</th>
                <th className="text-right px-4 py-2">Retail (éditable)</th>
                <th className="text-center px-4 py-2 w-24">Override</th>
              </tr>
            </thead>
            <tbody>
              {product.variants?.map((v) => (
                <VariantRow
                  key={v.variant_id}
                  productId={product.product_id}
                  variant={v}
                  onSave={onPriceSave}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const VariantRow = ({ productId, variant, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(
    variant.retail_amount_cents != null ? (variant.retail_amount_cents / 100).toFixed(2) : "",
  );
  const [saving, setSaving] = useState(false);

  const commit = async () => {
    const cents = Math.round(parseFloat(draft) * 100);
    if (!Number.isFinite(cents) || cents < 100) return;
    setSaving(true);
    try {
      await onSave(productId, variant.variant_id, cents);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr className="border-t border-[color:var(--gv-line)]" data-testid={`variant-row-${variant.variant_id}`}>
      <td className="px-4 py-2 text-[color:var(--gv-ink)]/85">{variant.title}</td>
      <td className="px-4 py-2 text-[11px] text-[color:var(--gv-ink)]/45 font-mono truncate max-w-[280px]" title={variant.product_uid}>
        {variant.product_uid}
      </td>
      <td className="px-4 py-2 text-right text-[color:var(--gv-ink)]/55">{fmtEUR(variant.wholesale_amount_cents)}</td>
      <td className="px-4 py-2 text-right">
        {editing ? (
          <div className="inline-flex items-center gap-1 justify-end">
            <input
              type="number" step="0.01" min="1"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
              className="gv-input w-24 text-right py-1 text-[13px]"
              autoFocus
              data-testid={`price-input-${variant.variant_id}`}
            />
            <span className="text-[12px] text-[color:var(--gv-ink)]/55">€</span>
            <button onClick={commit} disabled={saving} className="text-green-600 hover:bg-green-50 p-1 rounded"
                    data-testid={`price-save-${variant.variant_id}`}>
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            </button>
            <button onClick={() => setEditing(false)} className="text-red-500 hover:bg-red-50 p-1 rounded">
              <X size={14} />
            </button>
          </div>
        ) : (
          <button onClick={() => setEditing(true)} className="hover:text-[color:var(--gv-pink)] transition-colors font-medium"
                  data-testid={`price-display-${variant.variant_id}`}>
            {fmtEUR(variant.retail_amount_cents)}
          </button>
        )}
      </td>
      <td className="px-4 py-2 text-center">
        {variant.manual_override ? (
          <span className="text-[11px] text-[color:var(--gv-pink)] bg-[color:var(--gv-pink)]/10 px-1.5 py-0.5 rounded">manuel</span>
        ) : (
          <span className="text-[11px] text-[color:var(--gv-ink)]/40">auto</span>
        )}
      </td>
    </tr>
  );
};

export default AdminGelatoProductsPage;

/**
 * Modal editing the editorial metadata attached to a product — a badge
 * (one of the 9 canonical keys) and up to 6 "Pourquoi cette carte ?"
 * bullets. Stored in a separate `shop_meta` collection so Gelato sync
 * never overwrites it.
 */
const ShopMetaModal = ({ productId, title, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [badges, setBadges] = useState([]);
  const [badge, setBadge] = useState("");
  const [bullets, setBullets] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [meta, badgeList] = await Promise.all([
          adminApi.getShopMeta(productId),
          adminApi.listShopBadges(),
        ]);
        if (cancelled) return;
        setBadge(meta?.badge || "");
        // Pad to 4 rows so the admin sees empty inputs to fill without
        // having to click "add bullet" — 4 is our conversion target.
        const existing = meta?.why_bullets || [];
        const padded = [...existing, ...Array(Math.max(0, 4 - existing.length)).fill("")];
        setBullets(padded.slice(0, 6));
        setBadges(badgeList?.badges || []);
      } catch (e) {
        setError(e?.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [productId]);

  const setBulletAt = (idx, v) =>
    setBullets((prev) => prev.map((b, i) => (i === idx ? v : b)));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = { badge, why_bullets: bullets.filter((b) => (b || "").trim()) };
      await adminApi.saveShopMeta(productId, payload);
      onClose();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={() => !saving && onClose()}
    >
      <div
        className="bg-white rounded-[8px] w-full max-w-lg p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="shop-meta-modal"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-display text-xl text-[color:var(--gv-ink)] leading-tight">Meta éditoriale</h3>
            <div className="text-[12px] text-[color:var(--gv-ink)]/55 mt-1 line-clamp-1">{title}</div>
          </div>
          <button onClick={onClose} disabled={saving} className="p-1 rounded hover:bg-[color:var(--gv-mist)]">
            <X size={16} />
          </button>
        </div>

        {loading ? (
          <div className="py-8 text-center"><Loader2 size={20} className="animate-spin mx-auto text-[color:var(--gv-pink)]" /></div>
        ) : (
          <div className="space-y-4">
            <div>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-2">
                <Tag size={12} /> Badge principal
              </div>
              <select
                value={badge}
                onChange={(e) => setBadge(e.target.value)}
                className="gv-input w-full"
                data-testid="shop-meta-badge"
              >
                <option value="">— Aucun badge —</option>
                {badges.map((b) => (
                  <option key={b.key} value={b.key}>{b.emoji} {b.label}</option>
                ))}
              </select>
              <div className="text-[11px] text-[color:var(--gv-ink)]/50 mt-1.5">
                Un seul badge par produit — sert de signal de réassurance sur la vignette et la fiche.
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-2">
                <Sparkles size={12} /> Pourquoi cette carte ? <span className="text-[color:var(--gv-ink)]/35">(3-5 puces max)</span>
              </div>
              <div className="space-y-2">
                {bullets.map((b, i) => (
                  <input
                    key={i}
                    type="text"
                    value={b}
                    onChange={(e) => setBulletAt(i, e.target.value)}
                    placeholder={i === 0 ? "Pour comprendre les terroirs de…" : i === 1 ? "Pour préparer le WSET…" : "Pour offrir à un amateur…"}
                    maxLength={180}
                    className="gv-input w-full text-[13px]"
                    data-testid={`shop-meta-bullet-${i}`}
                  />
                ))}
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-[5px] text-[12.5px]">{error}</div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={onClose}
                disabled={saving}
                className="px-4 py-2 rounded-[4px] border border-[color:var(--gv-line)] text-[13px]"
                data-testid="shop-meta-cancel"
              >
                Annuler
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="px-4 py-2 rounded-[4px] bg-[color:var(--gv-ink)] text-white text-[13px] disabled:opacity-50"
                data-testid="shop-meta-save"
              >
                {saving && <Loader2 size={12} className="animate-spin inline mr-1" />}
                Enregistrer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
